import type { Face, Keypoint } from '@tensorflow-models/face-landmarks-detection';
import type { BoundingBox } from '@tensorflow-models/face-landmarks-detection/dist/shared/calculators/interfaces/shape_interfaces';

import type { FaceDetectResult } from '../types';

// face should cover 15-30% of image. In it's in range then send this image to BE
export const MIN_FACE_SIZE_PERCENT = 14;
export const MAX_FACE_SIZE_PERCENT = 30;

// const centerPercent = 50;
const allowedOffsetFromCenterPercent = 20;

function isLookingStraight(keyPoints: Keypoint[]) {
    const leftEye = keyPoints.find((point) => point.name === 'leftEye');
    const rightEye = keyPoints.find((point) => point.name === 'rightEye');
    const nose = keyPoints.find((point) => point.name === 'noseTip');
    const leftEar = keyPoints.find((point) => point.name === 'leftEarTragion');
    const rightEar = keyPoints.find((point) => point.name === 'rightEarTragion');

    if (!leftEye || !rightEye || !nose || !leftEar || !rightEar) {
        return false;
    }
    // Calculate distances between keyPoints
    const distanceEyes = Math.sqrt(
        Math.pow(rightEye.x - leftEye.x, 2) + Math.pow(rightEye.y - leftEye.y, 2),
    );
    const distanceEars = Math.sqrt(
        Math.pow(rightEar.x - leftEar.x, 2) + Math.pow(rightEar.y - leftEar.y, 2),
    );
    const distanceLeftEyeToNose = Math.sqrt(
        Math.pow(leftEye.x - nose.x, 2) + Math.pow(leftEye.y - nose.y, 2),
    );
    const distanceRightEyeToNose = Math.sqrt(
        Math.pow(rightEye.x - nose.x, 2) + Math.pow(rightEye.y - nose.y, 2),
    );
    const distanceLeftEarToNose = Math.sqrt(
        Math.pow(leftEar.x - nose.x, 2) + Math.pow(leftEar.y - nose.y, 2),
    );
    const distanceRightEarToNose = Math.sqrt(
        Math.pow(rightEar.x - nose.x, 2) + Math.pow(rightEar.y - nose.y, 2),
    );

    const eyeLineAngle =
        Math.atan2(leftEye.y - rightEye.y, leftEye.x - rightEye.x) * (180 / Math.PI);

    // Angle thresholds (in degrees)
    const maxRotationAngle = 20; // 20 degrees allowance for head rotation
    const minEyeToNoseRatio = 0.5; // Allow 50%-150% for nose misalignment
    const maxEyeToNoseRatio = 2.5;
    const maxNoseToEarRatio = 60;

    // Check if the nose is roughly centered horizontally
    const eyeToNoseRatio = distanceLeftEyeToNose / distanceRightEyeToNose;
    const isNoseCenteredHorizontally =
        eyeToNoseRatio > minEyeToNoseRatio && eyeToNoseRatio < maxEyeToNoseRatio;

    // Check if the eye line is close to horizontal
    const isEyeLineHorizontal = Math.abs(eyeLineAngle) <= maxRotationAngle;

    // Check if the distances between ears are reasonably consistent with the eyes
    const earToEyeRatio = distanceEars / distanceEyes;
    const isEarAlignmentConsistent =
        earToEyeRatio > minEyeToNoseRatio && earToEyeRatio < maxEyeToNoseRatio;

    const noseToYearDifferenceRatio =
        100 *
        Math.abs(
            (distanceRightEarToNose - distanceLeftEarToNose) /
                ((distanceLeftEarToNose + distanceRightEarToNose) / 2),
        );
    const isDistanceBetweenNoseAndEarsValid = noseToYearDifferenceRatio <= maxNoseToEarRatio;

    // Determine if the face is straight with a slight turn allowance
    return (
        isNoseCenteredHorizontally &&
        isEyeLineHorizontal &&
        isEarAlignmentConsistent &&
        isDistanceBetweenNoseAndEarsValid
    );
}

const isFaceInCenter = ({
    screenshotWidth,
    screenshotHeight,
    box,
}: {
    screenshotWidth: number;
    screenshotHeight: number;
    box: BoundingBox;
}) => {
    const leftBottomPos = { x: box.xMin, y: box.yMin };
    const leftTopPos = { x: box.xMin, y: box.yMax };
    const rightTopPos = { x: box.xMax, y: box.yMax };
    const rightBottomPos = { x: box.xMax, y: box.yMin };

    const xCenter = screenshotWidth / 2;
    const yCenter = screenshotHeight / 2;

    const { yMin, yMax } = box;

    const yTopDifference = Math.abs(yCenter - yMax);
    const yBottomDifference = Math.abs(yCenter - yMin);

    const maxYDifferentRatio = 175;
    const yDifferenceRatio =
        100 *
        Math.abs((yTopDifference - yBottomDifference) / ((yTopDifference + yBottomDifference) / 2));

    // check if each point of box in the corresponding section of the axis
    if (
        leftBottomPos.x < xCenter &&
        leftBottomPos.y < yCenter &&
        leftTopPos.x < xCenter &&
        leftTopPos.y > yCenter &&
        rightTopPos.x > xCenter &&
        rightTopPos.y > yCenter &&
        rightBottomPos.x > xCenter &&
        rightBottomPos.y < yCenter &&
        yDifferenceRatio <= maxYDifferentRatio
    ) {
        return true;
    }

    return false;
};

export const detectFace = (
    face: Face,
    video: {
        width: number;
        height: number;
    },
    minFaceResultPercent: number,
): FaceDetectResult => {
    const screenshotHeight = video.height;
    const screenshotWidth = video.width;

    const { box } = face;
    const facePercent = ((box.width * box.height) / (screenshotWidth * screenshotHeight)) * 100;

    const isCenter = isFaceInCenter({ screenshotHeight, screenshotWidth, box });
    const isStraight = isLookingStraight(face.keypoints);

    const getFacePercentHints = () => {
        if (
            facePercent >= MIN_FACE_SIZE_PERCENT &&
            facePercent >= minFaceResultPercent &&
            facePercent <= MAX_FACE_SIZE_PERCENT
        ) {
            return 'ok';
        }

        if (facePercent <= MAX_FACE_SIZE_PERCENT) {
            return 'small';
        }

        if (
            facePercent >= MAX_FACE_SIZE_PERCENT + allowedOffsetFromCenterPercent ||
            facePercent >= MAX_FACE_SIZE_PERCENT
        ) {
            return 'big';
        }

        return 'small';
    };

    const facePercentsHints = getFacePercentHints();
    const isSuccess = isCenter && isStraight && facePercentsHints === 'ok';

    const result: FaceDetectResult = {
        isSuccess,
        isCenter,
        isFaceFound: true,
        isLookingStraight: isStraight,
        facePercent: {
            value: facePercent || 0,
            hint: facePercentsHints,
        },
    };

    return result;
};
